
import React from "react";
import {
  MathpixLoader,
  MathpixMarkdown,
  MathpixMarkdownModel,
} from "mathpix-markdown-it";
import "katex/dist/katex.min.css";
import { sanitizeMathContent } from "../../helpers/mathContentSafety";
import "./MixedContentPreview.css";

// Mathpix/KaTeX sizes its glyphs in em, so the container's font-size is what
// scales an equation. Every caller's size is routed through --math-font-scale
// (see src/index.css) so notation stays proportional to the surrounding copy
// instead of each call site drifting on its own.
// Without an explicit size the equation tracks whatever copy surrounds it.
const DEFAULT_MATH_FONT_SIZE = '1em';

const scaledFontSize = (fontSize) => {
  const requested = typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
  return `calc(${requested || DEFAULT_MATH_FONT_SIZE} * var(--math-font-scale, 1))`;
};

// Characters a bare expression may contain. Anything outside this set (a
// backslash, brace, dollar, percent, hash, ampersand) means the line is either
// already LaTeX or something TeX would treat as markup, so it is left alone.
const MATH_ONLY_CHARS = /^[0-9a-zA-Z\s+\-*/=<>().,'^_|±×÷−≤≥≠√π°]+$/;

// Step work and stored answers reach us as bare expressions ("2x + 4 = 10")
// because the model returns calculations without delimiters. Rendered as prose
// they pick up the UI font, so a line that is nothing but notation is promoted
// to inline math. The test is deliberately narrow: a word of two or more
// letters is prose ("Solve for x"), and a line with no operator is not an
// expression, so both keep their existing plain-text rendering.
const isMathOnlyLine = (line) => {
  const text = line.trim();
  if (!text) return false;
  if (!/[0-9a-zA-Z]/.test(text)) return false;
  if (!/[+\-*/=<>^_]/.test(text)) return false;
  if (/[a-zA-Z]{2,}/.test(text)) return false;
  if (text.includes('**')) return false;
  return MATH_ONLY_CHARS.test(text);
};

// Helper function to format mixed content for MathpixMarkdown
const formatMixedContent = (text) => {
  if (!text) return '';

  // If content already has proper LaTeX delimiters, return as is
  if (text.includes('$') || text.includes('\\(') || text.includes('\\[')) {
    return text;
  }

  // Check if this looks like pure LaTeX (contains LaTeX commands)
  const latexPattern = /\\[a-zA-Z]+|[{}^_]|\\\\/;
  if (latexPattern.test(text)) {
    // Wrap in inline math delimiters if it's likely LaTeX
    return `$${text}$`;
  }

  // Mixed prose and notation: promote the notation-only lines so equations
  // render as math while the sentences around them stay prose.
  return text
    .split('\n')
    .map((line) => (isMathOnlyLine(line) ? `$${line.trim()}$` : line))
    .join('\n');
};

const MathpixScopeContext = React.createContext(false);

/**
 * Declares that one ancestor has already installed the Mathpix stylesheet, so
 * the previews inside it skip their own MathpixLoader.
 *
 * MathpixLoader injects that stylesheet on mount and renders a placeholder
 * until it is in place. That is the right trade for a single equation, but a
 * chat thread holds a preview per bubble, and one gate (plus one wrapper
 * element and one duplicated DOM id) per bubble is pure overhead. This scope
 * does the same install once, adds no markup of its own, and so cannot disturb
 * the layout of whatever it wraps.
 */
export function MathpixScope({ children }) {
  // Layout effect, not a plain effect: the stylesheet has to land before the
  // browser paints the equations it styles.
  React.useLayoutEffect(() => {
    MathpixMarkdownModel.loadMathJax();
  }, []);

  return (
    <MathpixScopeContext.Provider value={true}>
      {children}
    </MathpixScopeContext.Provider>
  );
}

function MixedContentPreview({ content, style, className, fontSize }) {
  const insideScope = React.useContext(MathpixScopeContext);

  // Sanitized after formatting, because formatting is what decides whether a
  // bare line is prose or notation. Memoized because rendering runs MathJax,
  // and this component is mounted once per chat bubble.
  const safeContent = React.useMemo(
    () => sanitizeMathContent(formatMixedContent(content)),
    [content]
  );

  const containerStyle = {
    ...style,
    fontSize: scaledFontSize(fontSize ?? style?.fontSize)
  };

  const markdown = <MathpixMarkdown text={safeContent} />;

  return (
    <div
      style={containerStyle}
      className={className ? `mixed-content-preview ${className}` : 'mixed-content-preview'}
    >
      {insideScope ? markdown : <MathpixLoader>{markdown}</MathpixLoader>}
    </div>
  );
}

export default React.memo(MixedContentPreview);
